import './datadog-rum';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, RouterModule } from '@angular/router';
import { routes } from './app-routing.module';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsModule } from '@ngxs/store';
import { environment } from 'environments/environment';
import { BorrowerSidebarState } from 'ng2-app/borrower-sidebar/borrower-sidebar.state';
import { FORMLY_CONFIG } from 'ng2-app/form-core/formly/formly-core.config';
import { ConfirmationDialogModule } from 'ng2-app/modules/confirmation-dialog/confirmation-dialog.module';
import { LetDirectiveModule } from 'ng2-app/modules/directives/let/let.directive.module';
import { UserThreadsState } from 'ng2-app/modules/today/state/email/user-threads.state';
import { UserSmsThreadsState } from 'ng2-app/modules/today/state/sms/user-sms-state';
import { TodayBadgeCountsState } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.state';
import { TodayPageState } from 'ng2-app/modules/today/state/today-page-state/today-page.state';
import { UserActionItemDetailState } from 'ng2-app/modules/today/state/user-action-item-detail.state';
import { UserActionItemsState } from 'ng2-app/modules/today/state/user-action-items.state';
import { StackableNotificationsModule } from 'ng2-app/notifications/stackable-notifications/stackable-notifications.module';
import { ContractRequestState } from 'ng2-app/services/contract-requests/contract-request.state';
import { WorkflowTasksState } from 'ng2-app/services/workflow-tasks/workflow-tasks.state';
import { ActivityStreamState } from 'ng2-app/store/activity/activity-stream.state';
import { BorrowerStatusesState } from 'ng2-app/store/borrowerStatuses/borrower-statuses.state';
import { FlashSpreadState } from 'ng2-app/store/flashspread/flashspread.state';
import { BulkDealStartState } from 'ng2-app/store/bulk-deal-start/bulk-deal-start.state';
import { StackableNotificationsState } from 'ng2-app/store/stackable-notifications/StackableNotifications.state';
import { QuillDivBlock } from 'ng2-app/util/quill/quill-div-block';

// Quill Editor Settings
import { QuillPlaintextClipboard } from 'ng2-app/util/quill/quill-plaintext-clipboard';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import Quill from 'quill';
Quill.register(QuillDivBlock, true);
Quill.register('modules/clipboard', QuillPlaintextClipboard, true);

// AG grid
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
ModuleRegistry.registerModules([AllEnterpriseModule]);

// Modules
import { AddNewLeadModule } from './add-new-lead/add-new-lead.module';
import { AppComponent } from './app.component';
import { BorrowerPageModule } from './borrower/borrower-page.module';
import { CallbarModule } from './callbar/callbar.module';
import { ChatState } from './chat/chat-store/chat.state';
import { AlertModule } from './components/alert/alert.module';
import { CxInteractionSidebarModule } from './cx-interaction/cx-interaction-form.module';
import { GlobalBorrowerTabsModule } from './global-borrower-tabs/global-borrower-tabs.module';
import { GlobalHeaderModule } from './global-header/global-header.module';
import { ApiHttpClient } from './http/clients/api';

// Misc
import { ApiAuthOnlyHttpClient } from './http/clients/api-auth-only';
import { LoadingModule } from './loading/loading.module';
import { MessageService } from './messages/message-service/message.service';
import { MessageState } from './messages/message-store/message.state';
import { BorrowerModule } from 'lendio-borrower/borrower.module';
import { FunnelService } from './modules/funnel/services/funnel.service';
import { OwnerApplicationModule } from './modules/owner-application/owner-application.module';
import { AuthenticationService } from 'lendio-session/services/authentication.service';
import { SessionService } from 'lendio-session/services/session.service';
import { EventBusService } from './modules/shared/services/event-bus.service';
import { PusherService } from './modules/shared/services/pusher.service';
import { StateService } from './modules/shared/services/state.service';
import { TaskService } from './modules/task-management/services/task.service';
import { LenderUserDealsService, TeamDealsService } from './modules/team-deals/services/team-deals.service';
import { TeamService } from './modules/teams/team.service';
import { TemplatePickerModule } from './modules/template-picker/template-picker.module';
import { UserCallHistoryState } from './modules/today/state/call-history/call-history.state';
import { TodayModule } from './modules/today/today.module';
import { NotificationsHistoryModule } from './notifications-history/notifications-history.module';
import { NotificationsModule } from './notifications/notifications.module';
import { RollbarErrorHandler, rollbarFactory, RollbarService } from './rollbar';

// Services
import { BorrowerValueService } from './services/borrower-value/borrower-value.service';
import { CreditReportsService } from './services/credit-reports/credit-reports.service';
import { DealFlagService } from './services/deal-flag/deal-flag.service';
import { DealNoteService } from './services/dealNotes/dealNote.service';
import { DocumentsService } from './services/documents/documents.service';
import { LocaleSettingService } from './services/i18n/locale-setting.service';
import { LinkService } from './services/links/links.service';
import { NotesService } from './services/notes/notes.service';
import { PerformanceService } from './services/performance/performance.service';
import { PhoneLocaleService } from './services/phone-locale.service';
import { QueueService } from './services/queue/queue.service';
import { SmsService } from './sms/sms-service/sms.service';
import { SmsState } from './sms/sms-store/sms.state';

// NGXS State
import { ActivityState } from './store/activity/activity.state';
import { AdminPhoneToolsState } from './store/admin-phone-tools/admin-phone-tools.state';
import { AdditionalOwnerState } from 'ng2-app/store/additional-owner/additional-owner.state';
import { BankAccountState } from './store/bank-account/bank-account.state';
import { BorrowerIndexState } from './store/borrower-index/borrower-index.state';
import { BorrowerStatusReasonState } from './store/borrower-status-reason/borrower-status-reason.state';
import { BorrowerValueState } from './store/borrower-value/borrower-value.state';
import { BorrowerAlertsState } from './store/borrower/alerts/borrower-alerts.state';
import { BorrowerLoansState } from './store/borrower/borrower-loans/borrower-loans.state';
import { BorrowerState } from './store/borrower/borrower/borrower.state';
import { BorrowerOpportunityHistoryState } from './store/borrower/opportunity-history/borrower-opportunity-history.state';
import { BusinessesState } from './store/businesses/businesses.state';
import { CallState } from './store/call/call.state';
import { ColumnDataState } from './store/column-data/column-data.state';
import { ContactState } from './store/contact/contact.state';
import { CountryDataState } from './store/country/country-data.state';
import { CreditBoxesState } from './store/credit-boxes/credit-boxes.state';
import { DebtScheduleState } from './store/debt-schedule/debt-schedule.state';
import { CreditReportsState } from './store/credit-reports/credit-reports.state';
import { CXInteractionState } from './store/cx-interaction/cx-interaction.state';
import { CXPerformanceState } from './store/cx-performance/cx-performance.state';
import { DealFlagState } from './store/deal-flag/deal-flag.state';
import { DealStatusesState } from './store/deal/statuses/deal-statuses.state';
import { ApplicationState } from './store/deal/application/application.state';
import { DealAlertsState } from './store/deal/deal-alerts/deal-alerts.state';
import { DealApplicationRequirementState } from './store/deal/deal-application-requirements/deal-application-requirement.state';
import { DealDocumentState } from './store/deal/deal-documents/deal-document.state';
import { DealState } from './store/deal/deal/deal.state';
import { DispositionState } from './store/disposition/disposition.state';
import { DealNoteState } from './store/dealNotes/dealNote.state';
import { DocumentState } from './store/documents/document.state';
import { DrawDealState } from './store/draw-deal/draw-deal.state';
import { DuplicateBorrowersState } from './store/duplicate-borrowers/duplicate-borrowers.state';
import { ErcLinkedBusinessState } from './store/erc-linked-business/erc-linked-business.state';
import { FeatureFlagsState } from './store/feature-flags/feature-flags.state';
import { BorrowerInspectionState } from './store/borrower-inspection/borrower-inspection.state';
import { FiltersState } from './store/filters/filters.state';
import { FundingNeedsState } from './store/funding-needs/funding-needs.state';
import { FundingDeskFunnelState } from './store/funnel/funding-desk-funnel.state';
import { FundingManagerFunnelState } from './store/funnel/funding-manager-funnel.state';
import { GlobalBorrowerTabsState } from './store/global-borrower-tabs/global-borrower-tabs.state';
import { EmailTemplateState } from './store/lender-user/email-templates/email-templates.state';
import { LenderUserState } from './store/lender-user/lender-user/lender-user.state';
import { SmsTemplateState } from './store/lender-user/sms-templates/sms-templates.state';
import { LoanProductsState } from './store/loan-product/loan-product.state';
import { LenderUserBorrowerPreferencesState } from 'ng2-app/store/lender-user-borrower-preferences/lender-user-borrower-preferences.state';
import { NonOwnerPersonnelState } from './store/non-owner-personnel/non-owner-personnel.state';
import { NoteState } from './store/notes/note.state';
import { NotificationState } from './store/notification/notification.state';
import { OcrolusState } from './store/ocrolus/ocrolus.state';
import { ApprovalsState } from './store/offers/approvals.state';
import { OfferOptionsState } from './store/offers/offer-options.state';
import { OfferState } from './store/offers/offer.state';
import { OpportunityState } from './store/opportunity/opportunity.state';
import { PayrollValidationState } from './store/payroll-validation/payroll-validation.state';
import { PerformanceState } from './store/performance/performance.state';
import { PulseState } from 'ng2-app/store/pulse/pulse.state';
import { PulseIndividualState } from 'ng2-app/store/pulse/pulse-individual.state';
import { PulseLeaderboardState } from 'ng2-app/store/pulse/pulse-leaderboard.state';
import { PulseTeamState } from 'ng2-app/store/pulse/pulse-team.state';
import { QuestionsState } from './store/question/question.state';
import { QueueState } from './store/queue/queue.state';
import { ReminderState } from './store/reminder/reminder.state';
import { SalesInventoryState } from './store/sales-inventory/sales-inventory.state';
import { SalesInventoryBorrowersState } from './store/sales-inventory/sales-inventory-borrowers.state';
import { TeamSummaryState } from './store/sales-inventory/team-summary.state';
import { TeamBorrowersState } from './store/sales-inventory/team-borrowers.state';
import { SettingsAlertsState } from './store/settings/alerts/settings-alerts.state';
import { StateState } from './store/state/state.state';
import { TaskManagementState } from './store/task-management/task-management.state';
import { TeamDealsState } from './store/team-deals/team-deals.state';
import { TeamState } from './store/team/team.state';
import { UserDirectoryState } from './store/user-directory/user-directory.state';
import { DeclineReasonState } from './store/decline-reasons/decline-reasons.state';
import { BorrowerInsightsState } from './store/ai/borrower-state/borrower-insights/borrower-insights.state';
import { NeedsAndConcernsState } from './store/ai/borrower-state/borrower-insights/needs-and-concerns/needs-and-concerns.state';
import { BorrowerInsightsService } from './services/ai/borrower-state/borrower-insights/borrower-insights.service';
import { AIChatSessionState } from './store/ai/borrower-state/chat/ai-chat.state';
import { AIChatService } from './services/ai/borrower-state/chat/ai-chat.service';
import { SuggestedResponsesState } from './store/ai/borrower-state/borrower-insights/suggested-responses';
import { BorrowerConciergeState } from './store/ai/borrower-concierge';
import { McpConfigState } from './store/ai/mcp-config/mcp-config.state';

// Allows for setting window.[key] for debugging and testing
declare global {
    interface Window {
        [key: string]: any;
    }
}

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        FormlyMaterialModule,
        FormlyModule.forRoot(FORMLY_CONFIG),
        AlertModule,
        AddNewLeadModule,
        BorrowerModule,
        BrowserAnimationsModule,
        CommonModule,
        ConfirmationDialogModule,
        CallbarModule,
        BorrowerPageModule,
        GlobalBorrowerTabsModule,
        GlobalHeaderModule,
        CxInteractionSidebarModule,
        LoadingModule,
        LetDirectiveModule,
        MatSidenavModule,
        NotificationsModule,
        NotificationsHistoryModule,
        OwnerApplicationModule,
        ReactiveFormsModule,
        RouterModule,
        TemplatePickerModule,
        TodayModule,
        NgxsModule.forRoot([
            ActivityState,
            ActivityStreamState,
            AdditionalOwnerState,
            AdminPhoneToolsState,
            AIChatSessionState,
            ApplicationState,
            ApprovalsState,
            BankAccountState,
            BorrowerAlertsState,
            BorrowerInsightsState,
            NeedsAndConcernsState,
            SuggestedResponsesState,
            BorrowerConciergeState,
            McpConfigState,
            BorrowerIndexState,
            BorrowerLoansState,
            BorrowerOpportunityHistoryState,
            BorrowerSidebarState,
            BorrowerState,
            BorrowerStatusReasonState,
            BorrowerStatusesState,
            BorrowerValueState,
            BulkDealStartState,
            BusinessesState,
            CXInteractionState,
            CXPerformanceState,
            CallState,
            ChatState,
            ColumnDataState,
            ContactState,
            ContractRequestState,
            CountryDataState,
            CreditBoxesState,
            CreditReportsState,
            DebtScheduleState,
            DealAlertsState,
            DealApplicationRequirementState,
            DealDocumentState,
            DealFlagState,
            DealNoteState,
            DealState,
            DealStatusesState,
            DispositionState,
            DocumentState,
            DrawDealState,
            DuplicateBorrowersState,
            EmailTemplateState,
            ErcLinkedBusinessState,
            FeatureFlagsState,
            BorrowerInspectionState,
            FiltersState,
            FlashSpreadState,
            FundingDeskFunnelState,
            FundingManagerFunnelState,
            FundingNeedsState,
            GlobalBorrowerTabsState,
            LenderUserBorrowerPreferencesState,
            LenderUserState,
            LoanProductsState,
            MessageState,
            NonOwnerPersonnelState,
            NoteState,
            NotificationState,
            OcrolusState,
            OfferOptionsState,
            OfferState,
            OpportunityState,
            PayrollValidationState,
            PerformanceState,
            PulseState,
            PulseIndividualState,
            PulseLeaderboardState,
            PulseTeamState,
            QuestionsState,
            QueueState,
            ReminderState,
            SalesInventoryState,
            SalesInventoryBorrowersState,
            TeamSummaryState,
            TeamBorrowersState,
            SettingsAlertsState,
            SmsState,
            SmsTemplateState,
            StackableNotificationsState,
            StateState,
            TaskManagementState,
            TeamDealsState,
            TeamState,
            TodayBadgeCountsState,
            TodayPageState,
            UserActionItemDetailState,
            UserActionItemsState,
            UserCallHistoryState,
            UserDirectoryState,
            DeclineReasonState,
            UserSmsThreadsState,
            UserThreadsState,
            WorkflowTasksState,
        ]),
        NgxsLoggerPluginModule.forRoot({
            disabled: true,
        }),
        NgxsReduxDevtoolsPluginModule.forRoot({
            // Install the Redux Devtools browser plugin to use this.
            // See https://lendio.atlassian.net/browse/SMART-799.
            disabled: environment?.environment === 'LIVE',
            maxAge: 5000,
        }),
        StackableNotificationsModule,
    ],
    providers: [
        AIChatService,
        ApiAuthOnlyHttpClient,
        ApiHttpClient,
        AuthenticationService,
        BorrowerInsightsService,
        BorrowerValueService,
        CurrencyPipe,
        CreditReportsService,
        DatePipe,
        DecimalPipe,
        DocumentsService,
        EventBusService,
        FunnelService,
        LinkService,
        LocaleSettingService,
        MessageService,
        NotesService,
        DealFlagService,
        DealNoteService,
        PerformanceService,
        PhoneLocaleService,
        PusherService,
        QueueService,
        SessionService,
        SmsService,
        StateService,
        TaskService,
        TeamDealsService,
        LenderUserDealsService,
        TeamService,
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'outline',
                floatLabel: 'always',
            },
        },
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: {
                duration: 3000,
            },
        },
        {
            provide: LOCALE_ID,
            deps: [LocaleSettingService],
            useFactory: (localeSettingService: LocaleSettingService) => localeSettingService.getLocale(),
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            deps: [LocaleSettingService],
            useFactory: (localeSettingService: LocaleSettingService) => localeSettingService.getCurrencyCode(),
        },
        {
            provide: ErrorHandler,
            useClass: RollbarErrorHandler,
        },
        {
            provide: RollbarService,
            useFactory: rollbarFactory,
        },
        provideEnvironmentNgxMask(),
        NgxMaskPipe,
        NgxMaskDirective,
        FormlyModule,
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(routes),
    ],
})
export class AppModule { }
